import { atom, type PrimitiveAtom, type WritableAtom } from 'jotai';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- Jotai RESET is a `unique symbol`; atom generics need `typeof RESET`, which requires a value binding
import { atomWithReset, atomWithStorage, createJSONStorage, RESET } from 'jotai/utils';

import type { CypherLspClient } from '../pages/labs/lsp/cypherClient';

const CYPHER_QUERY_STORAGE_KEY = 'graph-central-editor-cypher-query';
export const CypherQueryAtom: WritableAtom<
	string,
	[string | typeof RESET | ((prev: string) => string | typeof RESET)],
	void
> = atomWithStorage<string>(
	CYPHER_QUERY_STORAGE_KEY,
	'',
	createJSONStorage<string>(() => localStorage),
	{ getOnInit: true },
);
export const GraphQLQueryAtom: PrimitiveAtom<string> & {
	init: string;
} = atom<string>('');
export const UserStaffIdAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Parameter rows state
export type ParameterRowType = 'parameter' | 'header';

export type ParameterRowState = {
	id: string;
	type: ParameterRowType | null;
	name: string;
	value: string;
	enabled: boolean;
};

const randomRowId = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const createParameterRowState = (overrides?: Partial<ParameterRowState>): ParameterRowState => ({
	id: overrides?.id ?? randomRowId('row'),
	type: overrides?.type ?? null,
	name: overrides?.name ?? '',
	value: overrides?.value ?? '',
	enabled: overrides?.enabled ?? true,
});

const parameterRowIdsBaseAtom: PrimitiveAtom<string[]> & {
	init: string[];
} = atom<string[]>([]);
export const parameterRowsBaseAtom: PrimitiveAtom<Record<string, ParameterRowState>> & {
	init: Record<string, ParameterRowState>;
} = atom<Record<string, ParameterRowState>>({});

const rowsToHeadersMap = (rows: Record<string, ParameterRowState>): Record<string, string> => {
	const result: Record<string, string> = {};
	Object.values(rows).forEach((row) => {
		if (row.type !== 'header' || !row.enabled) {
			return;
		}
		const key = row.name.trim();
		if (key) {
			result[key] = row.value;
		}
	});
	return result;
};

const rowsToParamsMap = (rows: Record<string, ParameterRowState>): Record<string, unknown> => {
	const result: Record<string, unknown> = {};
	Object.values(rows).forEach((row) => {
		if (row.type !== 'parameter' || !row.enabled) {
			return;
		}
		const key = row.name.trim();
		if (key) {
			result[key] = row.value;
		}
	});
	return result;
};

const syncRowsForType = (
	get: any,
	set: any,
	type: ParameterRowType,
	entries: Record<string, unknown>,
) => {
	const rows = get(parameterRowsBaseAtom) as Record<string, ParameterRowState>;
	const ids = get(parameterRowIdsBaseAtom) as string[];

	const nextRows: Record<string, ParameterRowState> = {};
	const nextIds: string[] = [];

	ids.forEach((id) => {
		const row = rows[id];
		if (!row) {
			return;
		}
		if (row.type === type) {
			return;
		}
		nextRows[id] = row;
		nextIds.push(id);
	});

	Object.entries(entries).forEach(([rawName, rawValue]) => {
		const name = rawName.trim();
		if (!name) {
			return;
		}
		const value = rawValue == null ? '' : String(rawValue);
		let newRow = createParameterRowState({ type, name, value });
		while (nextRows[newRow.id]) {
			newRow = createParameterRowState({ type, name, value });
		}
		nextRows[newRow.id] = newRow;
		nextIds.push(newRow.id);
	});

	set(parameterRowsBaseAtom, nextRows);
	set(parameterRowIdsBaseAtom, nextIds);
};

export type ParameterRowUpdater =
	| Partial<ParameterRowState>
	| ((current: ParameterRowState) => ParameterRowState);

export const HeadersAtom: WritableAtom<Record<string, string>, [update: unknown], void> = atom(
	(get) => rowsToHeadersMap(get(parameterRowsBaseAtom) as Record<string, ParameterRowState>),
	(get, set, update) => {
		const previous = rowsToHeadersMap(
			get(parameterRowsBaseAtom) as Record<string, ParameterRowState>,
		);
		const resolved =
			(typeof update === 'function'
				? (update as (prev: Record<string, string>) => Record<string, string>)(previous)
				: update) ?? {};
		syncRowsForType(get, set, 'header', resolved as Record<string, unknown>);
	},
);

export const ParamsAtom: WritableAtom<Record<string, unknown>, [update: unknown], void> = atom(
	(get) => rowsToParamsMap(get(parameterRowsBaseAtom) as Record<string, ParameterRowState>),
	(get, set, update) => {
		const previous = rowsToParamsMap(
			get(parameterRowsBaseAtom) as Record<string, ParameterRowState>,
		);
		const resolved =
			(typeof update === 'function'
				? (update as (prev: Record<string, unknown>) => Record<string, unknown>)(previous)
				: update) ?? {};
		syncRowsForType(get, set, 'parameter', resolved as Record<string, unknown>);
	},
);

export const GraphQLLimitAtom: PrimitiveAtom<number> & {
	init: number;
} = atom<number>(1000);

// Mock errors atom - stores array of error type keys that should be injected
export type MockErrorType = 'something-went-wrong' | 'null-value-non-nullable' | 'validation-error';
export const MockErrorsAtom: PrimitiveAtom<MockErrorType[]> & {
	init: MockErrorType[];
} = atom<MockErrorType[]>([]);

export const UseExternalGraphQLEndpointAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);

// Execution state atoms - using atomWithReset for atoms that clear on execute
export const NetworkResultAtom: WritableAtom<any, [any], void> & {
	init: any;
} = atomWithReset<any>(undefined);
export const RunningAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);
export const SpotlightAtom: WritableAtom<
	'cypher' | 'graphql' | null,
	[
		| typeof RESET
		| 'cypher'
		| 'graphql'
		| ((prev: 'cypher' | 'graphql' | null) => typeof RESET | 'cypher' | 'graphql' | null)
		| null,
	],
	void
> & {
	init: 'cypher' | 'graphql' | null;
} = atomWithReset<'cypher' | 'graphql' | null>(null);
export const RoundTripMsAtom: PrimitiveAtom<number | null> & {
	init: number | null;
} = atom<number | null>(null);
export const AuthLoginUrlAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Editor state atoms
export type CypherEditorPosition = {
	line: number;
	column: number;
};

export const CypherEditorPositionAtom: PrimitiveAtom<CypherEditorPosition | null> & {
	init: CypherEditorPosition | null;
} = atom<CypherEditorPosition | null>(null);

export const CypherLspClientAtom: PrimitiveAtom<CypherLspClient | null> & {
	init: CypherLspClient | null;
} = atom<CypherLspClient | null>(null);

export type ReturnLabelEntry = { name: string; labels: string[] };

export const ReturnLabelsAtom: WritableAtom<
	ReturnLabelEntry[] | null,
	[
		| typeof RESET
		| ReturnLabelEntry[]
		| ((prev: ReturnLabelEntry[] | null) => typeof RESET | ReturnLabelEntry[] | null)
		| null,
	],
	void
> & {
	init: ReturnLabelEntry[] | null;
} = atomWithReset<ReturnLabelEntry[] | null>(null);

// Atom to store semantic errors from query execution
export type SemanticError = {
	message: string;
	line?: number;
	column?: number;
};

export const SemanticErrorsAtom: WritableAtom<
	SemanticError[],
	[typeof RESET | SemanticError[] | ((prev: SemanticError[]) => typeof RESET | SemanticError[])],
	void
> & {
	init: SemanticError[];
} = atomWithReset<SemanticError[]>([]);

// Atom to trigger formatting in the Cypher editor
// Atom to store a proposed Cypher query from the AI assistant (for diff view)
export type ProposedCypherQuery = {
	query: string;
};
// UGS schema loading error atom (nodes/relationships schema loading failed)
// Context API generation state atoms
export type ContextApiData = {
	name: string;
	description: string;
	cypherQuery: string;
	sampledResults: any;
	graphqlQuery: string;
};

// Exploration override: when non-null, ExecuteQueryAtom uses this instead of
// CypherQueryAtom so the editor's visible Cypher is never mutated during
// relationship discovery / loading.
export const ExplorationCypherOverrideAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Exploration GraphQL override: same idea — prevents the GraphQL editor
// from flickering during exploration queries.
export const ExplorationGraphQLOverrideAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Tracks the Cyphie mode used when the current Cypher query was generated by Cyphie.
// null = not from Cyphie, 'default' or 'reasoning' = from Cyphie in that mode.
export type CyphieMode = 'default' | 'reasoning';
export const CyphieModeAtom: PrimitiveAtom<CyphieMode | null> & {
	init: CyphieMode | null;
} = atom<CyphieMode | null>(null);

// Objects view query results - separate from NetworkResultAtom to avoid affecting Labs page
export const ObjectsQueryResultAtom: WritableAtom<any, [any], void> & {
	init: any;
} = atomWithReset<any>(undefined);

// Objects view running state
export const ObjectsQueryRunningAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);

// Abort controller for in-flight query requests — allows undo to cancel pending queries.
export const QueryAbortControllerAtom: PrimitiveAtom<AbortController | null> & {
	init: AbortController | null;
} = atom<AbortController | null>(null);
